import { Box, Portal, Stack, styled, Typography } from '@chronosphereio/chrono-ui';
import { memo, useMemo, useRef } from 'react';
import useResizeObserver from 'use-resize-observer';
import { TimeSeriesDataset, TooltipMode, UnitOptions } from '@/core';
import { ChartApi } from '../EChart';
import { EChartsDataFormat } from '../model';
import { assembleTransform, CursorCoordinates, SeriesMarker } from '../PanelTooltip';
import { legacyGetNearbySeriesData } from './nearby-series-legacy';
import { TimeSeriesTooltipHeader } from './TimeSeriesTooltipHeader';
import {
  NEARBY_SERIES_DESCRIPTION,
  TOOLTIP_BG_COLOR_FALLBACK,
  TOOLTIP_LABELS_MAX_WIDTH,
  TOOLTIP_MAX_WIDTH,
  TOOLTIP_MIN_WIDTH,
  TOOLTIP_NEARBY_SERIES_MAX_HEIGHT,
  TooltipConfig,
  TooltipSeriesArray,
} from './tooltip-model';
import { useMousePosition } from './utils';

export type LegacyTooltipConfig = {
  mode: TooltipMode;
  wrapSeriesName?: boolean;
  enablePinning?: boolean;
  hidden?: boolean;
};

export interface LegacyTooltipProps {
  chartData: EChartsDataFormat;
  chartApi: ChartApi;
  enablePinning?: boolean;
  tooltipConfig: TooltipConfig;
  unit?: UnitOptions;
  onUnpinClick?: () => void;
  pinnedPos: CursorCoordinates | null;
  /**
   * The id of the container that will have the chart tooltip appended to it.
   * By default, the chart tooltip is attached to document.body.
   */
  containerId?: string;
}

const dataset: TimeSeriesDataset[] = [];
export const LineChartTooltip = memo(function LineChartTooltip({
  chartApi,
  chartData,
  enablePinning = true,
  tooltipConfig,
  unit,
  onUnpinClick,
  pinnedPos,
  containerId,
}: LegacyTooltipProps) {
  const totalSeries = chartData.timeSeries.length;
  const mousePos = useMousePosition(totalSeries);

  const { height, width, ref: tooltipRef } = useResizeObserver();

  const transform = useRef<string | undefined>();

  const isTooltipPinned = pinnedPos !== null && enablePinning;

  if (!mousePos || mousePos.target === null) return null;

  // Ensure user is hovering over a chart before checking for nearby series.
  if (pinnedPos === null && (mousePos.target as HTMLElement).tagName !== 'CANVAS') return null;

  // Get series nearby the cursor and pass into tooltip content children.
  const nearbySeries = legacyGetNearbySeriesData({
    mousePos,
    chartData,
    pinnedPos,
    chartApi,
    unit,
  });
  if (nearbySeries.length === 0) {
    return null;
  }

  const containerElement = containerId ? document.querySelector(containerId) : undefined;
  // if tooltip is attached to a container, set max height to the height of the container so tooltip does not get cut off
  const maxHeight = containerElement ? containerElement.getBoundingClientRect().height : undefined;
  if (!isTooltipPinned) {
    transform.current = assembleTransform({
      mousePos,
      pinnedPos,
      tooltipHeight: height ?? 0,
      tooltipWidth: width ?? 0,
      containerElement,
    });
  }

  return (
    <Portal container={containerElement}>
      <Box
        ref={tooltipRef}
        sx={(theme) => ({
          minWidth: TOOLTIP_MIN_WIDTH.large,
          maxWidth: TOOLTIP_MAX_WIDTH,
          maxHeight: maxHeight ?? TOOLTIP_NEARBY_SERIES_MAX_HEIGHT,
          padding: 0,
          position: 'absolute',
          top: 0,
          left: 0,
          backgroundColor: theme.palette.designSystem?.grey[800] ?? TOOLTIP_BG_COLOR_FALLBACK,
          borderRadius: '6px',
          color: '#fff',
          fontSize: '11px',
          visibility: 'visible',
          opacity: 1,
          transition: 'all 0.1s ease-out',
          // Ensure pinned tooltip shows behind edit panel drawer and sticky header
          zIndex: pinnedPos !== null ? 'auto' : theme.zIndex.tooltip,
          overflow: 'hidden',
          '&:hover': {
            overflowY: 'auto',
          },
        })}
        style={{
          transform: transform.current,
          display: transform.current ? 'block' : 'none',
        }}
      >
        <Stack spacing={0.5} padding={(theme) => theme.spacing(1.5, 2)}>
          <TimeSeriesTooltipHeader
            series={nearbySeries}
            dataset={dataset}
            enablePinning={enablePinning}
            isTooltipPinned={isTooltipPinned}
            onUnpinClick={onUnpinClick}
          />
          <LegacyTooltipContent series={nearbySeries} tooltipConfig={tooltipConfig} />
        </Stack>
      </Box>
    </Portal>
  );
});

function LegacyTooltipContent(props: { series: TooltipSeriesArray | null; tooltipConfig: LegacyTooltipConfig }) {
  const { series, tooltipConfig } = props;

  const { wrapSeriesName } = tooltipConfig;

  const sortedNearbySeries = useMemo(() => {
    if (series === null) return null;
    const newSeriesArray = series.slice();
    return newSeriesArray.sort((a, b) => {
      if (a.seriesIndex === null || b.seriesIndex === null) return 0;
      return b.y - a.y;
    });
  }, [series]);

  if (series === null || sortedNearbySeries === null) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'table',
        width: '100%',
      }}
    >
      {sortedNearbySeries.map(
        ({ dataIndex, seriesIndex, seriesName, x, y, formattedY, markerColor, shouldEmphasizeText }) => {
          if (dataIndex === null || seriesIndex === null) return null;
          const key = seriesIndex.toString() + dataIndex.toString();

          return (
            <LegacySeriesInfo
              key={key}
              seriesName={seriesName}
              y={y}
              x={x}
              formattedY={formattedY}
              markerColor={markerColor}
              wrapSeriesName={wrapSeriesName}
              emphasizeText={shouldEmphasizeText}
            />
          );
        }
      )}
    </Box>
  );
}

const LegacySeriesInfo = memo(function SeriesInfo(props: {
  seriesName: string;
  y: number;
  x: number;
  formattedY?: string;
  markerColor: string;
  emphasizeText?: boolean;
  wrapSeriesName?: boolean;
}) {
  const { seriesName, formattedY, markerColor, emphasizeText = false, wrapSeriesName = true } = props;

  // Add space after commas so whiteSpace style to wrap series name to multiple lines works
  const formattedSeriesInfo = wrapSeriesName ? seriesName.replace(/[,]/g, ', ') : seriesName;

  return (
    <Box
      sx={{
        display: 'table-row',
      }}
    >
      <Box
        paddingY={0}
        sx={{
          display: 'table-cell',
          maxWidth: '520px',
        }}
      >
        <Stack flexDirection="row" alignItems="center">
          <SeriesMarker color={markerColor} />
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 'calc(100% - 25px)',
              minWidth: 150,
              maxWidth: TOOLTIP_LABELS_MAX_WIDTH,
            }}
            aria-label={NEARBY_SERIES_DESCRIPTION}
          >
            <LegacySeriesInfoTypography
              variant="body2"
              emphasizeText={emphasizeText}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: wrapSeriesName ? 'normal' : 'nowrap',
              }}
            >
              {formattedSeriesInfo}
            </LegacySeriesInfoTypography>
          </Box>
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'table-cell',
          paddingLeft: 1.5,
          textAlign: 'right',
          verticalAlign: 'middle',
        }}
        aria-label="tooltip series value"
      >
        <LegacySeriesInfoTypography variant="body2" sx={{ lineHeight: '24px' }}>
          {formattedY}
        </LegacySeriesInfoTypography>
      </Box>
    </Box>
  );
});

const LegacySeriesInfoTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'emphasizeText',
})<{ emphasizeText?: boolean }>(({ theme, emphasizeText }) => ({
  // Use bold font weight so emphasized series in tooltip is obvious in dark mode.
  fontWeight: emphasizeText ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  lineHeight: '24px',
}));
